/**
 * The sign-in gate. Mounts the app only for an authenticated Entra user.
 *
 * When the environment has auth disabled this is a passthrough, so the UI is
 * identical to today's — that is what lets the same image ship to every
 * environment while they are switched on one at a time.
 *
 * The gate proves AUTHENTICATION only. If the backend later rejects the token,
 * that is a gateway/authorizer problem, not a sign-in problem.
 *
 * LAYOUT (2026-08-21, Jack picked option B from the mockup set): a split panel —
 * a Midnight Blue field carrying the product name, and a narrow sign-in column on
 * Warm White. It reads as a product front door rather than a lock screen, and the
 * two halves give the name somewhere to live that isn't stacked on the button.
 *
 * Palette is Brand Standards v1.1: Midnight Blue #1B2A58, Cobalt #005DAA, Warm
 * White #FBF6F3. The previous screen used #1b365d (the internal dashboard navy)
 * and #a9843f (flat gold, which the guide rules out — gold is a gradient only).
 */
import { useEffect, useState } from "react";
import { AUTH_ENABLED } from "../services/authConfig";
import { getAuthError, getCurrentUser, signIn, signOut } from "../services/auth";
import { probeGatewayAccess } from "../services/api";
import LennarWordmark from "./LennarWordmark";
import "./auth.css";

/** Microsoft's four-square, drawn rather than fetched — it must not depend on a
 *  remote asset, and it tells people this is a Microsoft sign-in before they read
 *  the label. Brand-correct quadrant colors, presentational only. */
function MsMark() {
  return (
    <span className="auth-ms" aria-hidden="true">
      <i /><i /><i /><i />
    </span>
  );
}

/**
 * The shell. `left` is the navy field; children are the sign-in column, so the
 * error state and the sign-in state share one layout instead of two.
 */
function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-screen">
      <div className="auth-split">
        <div className="auth-brand">
          <LennarWordmark className="auth-wm" />
          <div className="auth-brand-body">
            <h1 className="auth-title">Email Creative Hub</h1>
            <p className="auth-tag">Request, build and send division email in one place.</p>
          </div>
        </div>
        <div className="auth-pane">
          <div className="auth-col">{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * ── THE GATEWAY IS ASKED AT THE FRONT DOOR (2026-09-22, Alexis Rashid, ATL) ──────────
 * Sign-in proves the Microsoft side. The gateway can still refuse the account (401/403
 * from its authorizer) and until today the first place that showed was the submit —
 * and not even there, because the submit read the refusal as success. Alexis built and
 * "submitted" several requests on prod that never reached the backend.
 * Now one cheap authenticated GET runs once per sign-in. A 401/403 swaps the app for a
 * screen that names the account and the gateway's answer, so the person knows before
 * they build anything and Farhan knows exactly which account to fix. Anything else —
 * a 200, a backend error, a network failure — lets the app through: only the gateway's
 * own refusal is a verdict on the account.
 */
export function GatewayCheck({ children, email, retryMs = 3000 }: { children: React.ReactNode; email: string | null; retryMs?: number }) {
  const [denied, setDenied] = useState<{ status: number; message?: string } | null>(null);
  /* ── "CAN'T REACH" IS ALSO A VERDICT (2026-09-22, later the same day) ─────────────
     The gateway's access log for every /emailcreativehub route showed NO request from
     Alexis at all — not a POST, not a preflight, on any route. Her browser never reached
     the gateway, and the app let her build a whole email on its built-in snapshots and
     then said "submitted". So a probe that cannot connect is not "let them through"; it
     is tried twice, three seconds apart, and then shown as its own screen with Retry. */
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    const run = async (n: number) => {
      const p = await probeGatewayAccess();
      if (!live) return;
      if (p.verdict === "denied") { setDenied({ status: p.status, message: p.message }); return; }
      if (p.verdict === "unknown") {
        if (n === 0) { setTimeout(() => { if (live) void run(1); }, retryMs); return; }
        setUnreachable(true);
        return;
      }
      setUnreachable(false);
    };
    void run(0);
    return () => { live = false; };
  }, [attempt, retryMs]);
  if (denied) {
    return (
      <Screen>
        <div className="auth-kicker">Not authorized</div>
        <h2 className="auth-lede">This account is signed in, but the Email Creative Hub API refused it.</h2>
        <p className="auth-detail" data-testid="gateway-denied">
          {email || "This account"} · {denied.status}{denied.message ? " · " + denied.message : ""}
        </p>
        <p className="auth-detail">Contact your RMT's Digital Ops Lead with this screen.</p>
        <button className="auth-btn" onClick={() => signOut()}>Sign out</button>
      </Screen>
    );
  }
  if (unreachable) {
    return (
      <Screen>
        <div className="auth-kicker">Can't reach the API</div>
        <h2 className="auth-lede">You're signed in, but this browser can't reach the Email Creative Hub API.</h2>
        <p className="auth-detail" data-testid="gateway-unreachable">
          {email || "This account"} · no response from the API. Requests can't be filed from here until it connects — check VPN or network and retry, or contact your RMT's Digital Ops Lead with this screen.
        </p>
        <button className="auth-btn" onClick={() => { setUnreachable(false); setAttempt((a) => a + 1); }}>Retry</button>
      </Screen>
    );
  }
  return <>{children}</>;
}

export default function AuthGate({ children }: { children: React.ReactNode }) {
  const [redirecting, setRedirecting] = useState(false);
  const error = getAuthError();

  if (!AUTH_ENABLED) return <>{children}</>;

  if (error) {
    return (
      <Screen>
        <div className="auth-kicker">Sign-in failed</div>
        <h2 className="auth-lede">Something went wrong on the way back from Microsoft.</h2>
        <p className="auth-detail auth-detail-err">{error}</p>
        <button className="auth-btn" onClick={() => window.location.reload()}>
          Try again
        </button>
      </Screen>
    );
  }

  const user = getCurrentUser();
  if (!user) {
    return (
      <Screen>
        <div className="auth-kicker">Sign in</div>
        <h2 className="auth-lede">Available to authorized Lennar team members.</h2>
        <button
          className="auth-btn"
          disabled={redirecting}
          onClick={() => {
            setRedirecting(true);
            signIn();
          }}
        >
          {!redirecting && <MsMark />}
          {redirecting ? "Redirecting…" : "Sign in with Microsoft"}
        </button>
        <p className="auth-detail">
          You'll be sent to Microsoft to sign in with your Lennar account.
        </p>
      </Screen>
    );
  }

  return <GatewayCheck email={user.email}>{children}</GatewayCheck>;
}
